"use client";

import { useState, useRef, useEffect } from "react";
import { Camera, Loader2, Bot, Plus, X, Check } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Card, CardContent } from "../components/ui/card";
import { useToast } from "../hooks/use-toast";
import { useAssistantAvatar } from "../contexts/AssistantAvatarContext";
import { useAuth } from "@workos-inc/authkit-react";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { useColor } from "@/contexts/ColorContext";
import { appConfig, welcomeMessageTemplate } from "@/appConfig";
import api from "../http";

// Modal component for creating new assistant
function CreateAssistantModal({
  isOpen,
  onClose,
  onCreate,
  isLoading,
  appColor,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (
    name: string,
    description: string | undefined,
    photo: File | null
  ) => Promise<void>;
  isLoading: boolean;
  appColor: string;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const descriptionValue = description.trim();
    await onCreate(name.trim(), descriptionValue ? descriptionValue : undefined, photo);
    setName("");
    setDescription("");
    setPhoto(null);
    setPhotoPreview(null);
    onClose();
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) return;
      if (file.size > 5 * 1024 * 1024) return;
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearPhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-[500px] relative max-h-[90vh] overflow-y-auto">
        <div className="p-4 lg:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Neuen Assistenten erstellen</h2>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none text-black hover:text-gray-700 transition-colors disabled:opacity-50"
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Photo Upload Section */}
            <div className="flex flex-col items-center">
              <div className="relative group mb-3">
                <Avatar className="h-24 w-24 ring-2 ring-slate-200">
                  {photoPreview ? (
                    <AvatarImage src={photoPreview} alt="Preview" className="object-cover" />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-br from-slate-100 to-slate-200">
                      <Bot className="h-10 w-10 text-slate-400" />
                    </AvatarFallback>
                  )}
                </Avatar>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <Camera className="h-6 w-6 text-white" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium transition-colors border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 h-8 px-3"
                >
                  Foto auswählen
                </button>
                {photo && (
                  <button
                    type="button"
                    onClick={clearPhoto}
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium transition-colors border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 h-8 px-3"
                  >
                    Entfernen
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">Optional · JPG, PNG (max. 5MB)</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
              />
            </div>

            {/* Name Input */}
            <div>
              <label
                htmlFor="assistant-name"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                Assistentenname
              </label>
              <input
                id="assistant-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z.B. Support Assistent"
                className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ "--tw-ring-color": appColor } as React.CSSProperties}
                autoFocus
              />
            </div>

            {/* Description Input */}
            <div>
              <label
                htmlFor="assistant-description"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                Beschreibung
              </label>
              <textarea
                id="assistant-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Kurze Beschreibung des Assistenten"
                className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                style={{ "--tw-ring-color": appColor } as React.CSSProperties}
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border bg-transparent h-10 px-4"
                style={{ borderColor: appColor, color: appColor }}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={isLoading || !name.trim()}
                className="flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-4"
                style={{ backgroundColor: !name.trim() ? `${appColor}40` : appColor }}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Erstellen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { toast } = useToast();
  const {
    assistants,
    currentAssistant,
    isLoading,
    uploadAssistantPhoto,
    createAssistant,
    updateAssistantName,
    updateAssistant: updateAssistantRaw,
    deleteAssistant,
    setCurrentAssistant,
    refreshAssistants,
  } = useAssistantAvatar();
  const { getAccessToken, user, role } = useAuth();
  const updateAssistant = updateAssistantRaw as (
    assistantId: number,
    updates: {
      name?: string;
      sharepoint_root_folder?: string;
      welcome_message?: string | null;
      description?: string | null;
    }
  ) => Promise<void>;
  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;
  const canEditDescription = role === "admin" || role === "superadmin";

  // Current user state for settings dropdown
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);
  // Profile image modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const [selectedAssistantId, setSelectedAssistantId] = useState<number | null>(
    null,
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [uploadingForAssistantId, setUploadingForAssistantId] = useState<number | null>(null);

  // Load current user information
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Fehler beim Laden des aktuellen Benutzers:", error);
      }
    };

    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  useEffect(() => {
    if (currentAssistant) {
      setSelectedAssistantId(currentAssistant.id);
    }
  }, [currentAssistant]);

  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);
  };

  const handleCreateAssistant = async (
    name: string,
    description: string | undefined,
    photo: File | null
  ) => {
    try {
      const newAssistant = await createAssistant(name, description);
      // Upload photo if provided
      if (photo && newAssistant?.id) {
        await uploadAssistantPhoto(newAssistant.id, photo);
      }
      await refreshAssistants();
      toast({
        title: "Erfolgreich",
        description: "Assistent erfolgreich erstellt.",
      });
    } catch (error) {
      console.error("Failed to create assistant:", error);
      toast({
        title: "Fehler",
        description: "Assistent konnte nicht erstellt werden.",
        variant: "destructive",
      });
    }
  };

  const handleRenameAssistant = async (assistantId: number, name: string) => {
    if (!name.trim()) return;
    try {
      await updateAssistantName(assistantId, name.trim());
      await refreshAssistants();
    } catch (error) {
      console.error("Failed to rename assistant:", error);
      toast({
        title: "Fehler",
        description: "Assistent konnte nicht umbenannt werden.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAssistant = async (assistantId: number) => {
    try {
      await deleteAssistant(assistantId);
      if (selectedAssistantId === assistantId) {
        setSelectedAssistantId(null);
      }
      await refreshAssistants();
    } catch (error) {
      console.error("Failed to delete assistant:", error);
      toast({
        title: "Fehler",
        description: "Assistent konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  const handleFileSelectForAssistant = async (file: File, assistantId: number) => {
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Ungültiger Dateityp",
        description: "Bitte wählen Sie eine Bilddatei aus (JPEG, PNG, etc.)",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Datei zu groß",
        description: "Bitte wählen Sie ein Bild kleiner als 5MB aus",
        variant: "destructive",
      });
      return;
    }

    setUploadingForAssistantId(assistantId);
    try {
      await uploadAssistantPhoto(assistantId, file);
      toast({
        title: "Erfolgreich",
        description: "Assistenten-Foto erfolgreich aktualisiert",
      });
      await refreshAssistants();
    } catch (error) {
      toast({
        title: "Upload fehlgeschlagen",
        description:
          "Fehler beim Hochladen des Assistenten-Fotos. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    } finally {
      setUploadingForAssistantId(null);
    }
  };

  // Create refs for each assistant's file input
  const fileInputRefs = useRef<Map<number, HTMLInputElement>>(new Map());
  const welcomeMessageRefs = useRef<Map<number, HTMLTextAreaElement>>(new Map());

  const handleFileInputForAssistant = (event: React.ChangeEvent<HTMLInputElement>, assistantId: number) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelectForAssistant(file, assistantId);
    }
    // Reset input value so same file can be selected again
    event.target.value = "";
  };

  const handleUploadClickForAssistant = (assistantId: number) => {
    const input = fileInputRefs.current.get(assistantId);
    input?.click();
  };

  const handleWelcomeMessageUpdate = async (
    assistantId: number,
    message: string,
  ) => {
    try {
      await updateAssistant(assistantId, {
        welcome_message: message.trim() === "" ? "" : message,
      });
      toast({
        title: "Gespeichert",
        description: "Willkommensnachricht wurde aktualisiert.",
      });
    } catch (error) {
      console.error("Failed to save welcome message:", error);
      toast({
        title: "Fehler",
        description: "Willkommensnachricht konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };

  const handleResetWelcomeMessage = async (assistantId: number) => {
    try {
      await updateAssistant(assistantId, { welcome_message: "" });
      const textarea = welcomeMessageRefs.current.get(assistantId);
      if (textarea) {
        textarea.value = welcomeMessageTemplate;
      }
      toast({
        title: "Zurückgesetzt",
        description: "Standard-Text wird wieder verwendet.",
      });
    } catch (error) {
      console.error("Failed to reset welcome message:", error);
      toast({
        title: "Fehler",
        description: "Willkommensnachricht konnte nicht zurückgesetzt werden.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col h-full min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <header className="flex items-center justify-between px-8 py-4 border-b border-gray-200 bg-gradient-to-b from-slate-50 via-white to-slate-50">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Assistent Einstellungen
          </h1>
          <p className="text-sm text-slate-500">
            Verwalten Sie Ihre Assistenten, Namen, Fotos und Willkommensnachrichten.
          </p>
        </div>
        <SettingsDropdown
          onOpenProfileModal={() => setIsProfileModalOpen(true)}
          currentUser={currentUser}
        />
      </header>

      <div className="flex-1 overflow-auto">
        <div className="container mx-auto p-6 max-w-4xl">
          {/* Header with Add Button */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex flex-col gap-1">
              <span className="text-lg font-medium">
                Assistenten
              </span>
              <div
                className="flex items-center rounded-[6px] px-[6px] py-[3px] w-fit"
                style={{ backgroundColor: `${APP_COLOR}15` }}
              >
                <span
                  className="font-bold text-xs"
                  style={{ color: APP_COLOR }}
                >
                  {assistants.length}{" "}
                  {assistants.length === 1 ? "Assistent" : "Assistenten"}
                </span>
              </div>
            </div>
            {role === "superadmin" && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-4 py-2"
                style={{ backgroundColor: APP_COLOR }}
              >
                <Plus size={16} className="mr-2" />
                <span>Neuer Assistent</span>
              </button>
            )}
          </div>

          {/* Assistants List - Full Width Cards */}
          <div className="space-y-4">
            {[...assistants]
              .sort((a, b) => {
                // Active assistant always on top
                if (a.id === currentAssistant?.id) return -1;
                if (b.id === currentAssistant?.id) return 1;
                return 0;
              })
              .map((assistant) => {
              const isCurrent = currentAssistant?.id === assistant.id;
              const isUploading = uploadingForAssistantId === assistant.id;

              return (
                <Card
                  key={assistant.id}
                  className="relative overflow-hidden transition-all"
                  style={{
                    borderColor: isCurrent ? APP_COLOR : undefined,
                    borderWidth: isCurrent ? "2px" : undefined,
                    boxShadow: isCurrent ? `0 4px 20px ${APP_COLOR}20` : undefined,
                  }}
                >
                  {/* Active Badge */}
                  {isCurrent && (
                    <div className="absolute top-4 right-4 z-10">
                      <span
                        className="inline-flex items-center gap-1 rounded-full text-white px-3 py-1.5 text-xs font-medium"
                        style={{ backgroundColor: APP_COLOR }}
                      >
                        <Check className="h-3 w-3" />
                        Aktiv
                      </span>
                    </div>
                  )}

                  <CardContent className="p-6">
                    <div className="flex items-center gap-6">
                      {/* Avatar Section with Upload */}
                      <div className="flex-shrink-0">
                        <div className="relative group">
                          <Avatar
                            className="h-24 w-24"
                            style={{
                              boxShadow: isCurrent
                                ? `0 0 0 4px ${APP_COLOR}20`
                                : "0 0 0 2px #e2e8f0",
                            }}
                          >
                            {isUploading ? (
                              <AvatarFallback className="animate-pulse bg-slate-100">
                                <Loader2
                                  className="h-10 w-10 animate-spin"
                                  style={{ color: APP_COLOR }}
                                />
                              </AvatarFallback>
                            ) : (
                              <>
                                <AvatarImage
                                  src={assistant.photo_url || undefined}
                                  alt={assistant.name}
                                  className="object-cover"
                                />
                                <AvatarFallback className="bg-gradient-to-br from-slate-100 to-slate-200">
                                  <Bot className="h-10 w-10 text-slate-400" />
                                </AvatarFallback>
                              </>
                            )}
                          </Avatar>
                          {/* Upload Overlay - Admin and Superadmin */}
                          {(role === "superadmin" || role === "admin") && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleUploadClickForAssistant(assistant.id)}
                                disabled={isUploading}
                                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                              >
                                <Camera className="h-6 w-6 text-white" />
                              </button>
                              {/* Hidden File Input */}
                              <input
                                ref={(el) => {
                                  if (el) fileInputRefs.current.set(assistant.id, el);
                                }}
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileInputForAssistant(e, assistant.id)}
                                className="hidden"
                              />
                            </>
                          )}
                        </div>
                        {(role === "superadmin" || role === "admin") && (
                          <p className="text-xs text-slate-400 mt-2 text-center">
                            Foto ändern
                          </p>
                        )}
                      </div>

                      {/* Info Section */}
                      <div className="flex-1 min-w-0">
                        {role === "superadmin" ? (
                          <input
                            className="w-full text-xl font-semibold text-slate-900 border-b-2 border-transparent hover:border-slate-200 focus:outline-none transition-colors bg-transparent mb-1"
                            style={{
                              borderColor: "transparent",
                            }}
                            onFocus={(e) => (e.target.style.borderColor = APP_COLOR)}
                            onBlur={(e) => {
                              e.target.style.borderColor = "transparent";
                              handleRenameAssistant(assistant.id, e.target.value);
                            }}
                            defaultValue={assistant.name}
                          />
                        ) : (
                          <h3 className="w-full text-xl font-semibold text-slate-900 mb-1">
                            {assistant.name}
                          </h3>
                        )}
                        {(canEditDescription || assistant.description) && (
                          <div className="mt-2">
                            <label className="text-xs text-slate-500 block mb-1">
                              Beschreibung
                            </label>
                            {canEditDescription ? (
                              <textarea
                                className="w-full text-sm text-slate-700 border border-slate-200 rounded-md px-2 py-2 focus:outline-none focus:border-slate-400 transition-colors bg-white resize-none"
                                placeholder="Beschreibung hinzufügen"
                                onBlur={(e) => {
                                  updateAssistant(assistant.id, {
                                    description: e.target.value.trim(),
                                  });
                                }}
                                defaultValue={assistant.description || ""}
                                rows={3}
                              />
                            ) : (
                              <p className="text-sm text-slate-600">
                                {assistant.description}
                              </p>
                            )}
                          </div>
                        )}
                        {/* SharePoint Root Folder - only show if SharePoint is enabled */}
                        {appConfig.features.sharePoint && (
                          <div className="mt-2">
                            <label className="text-xs text-slate-500 mb-1 block">
                              SharePoint Ordner
                            </label>
                            {role === "superadmin" ? (
                              <input
                                className="w-full text-sm text-slate-700 border border-slate-200 rounded-md px-2 py-1 focus:outline-none focus:border-slate-400 transition-colors bg-white"
                                placeholder="/Pfad/zum/Ordner"
                                onBlur={(e) => {
                                  updateAssistant(assistant.id, {
                                    sharepoint_root_folder: e.target.value,
                                  });
                                }}
                                defaultValue={assistant.sharepoint_root_folder || ""}
                              />
                            ) : (
                              <p className="text-sm text-slate-700">
                                {assistant.sharepoint_root_folder || "-"}
                              </p>
                            )}
                          </div>
                        )}

                        {(role === "admin" || role === "superadmin") && (
                          <div className="mt-4">
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-xs text-slate-500 block">
                                Willkommensnachricht
                              </label>
                              <span
                                className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
                                style={{
                                  backgroundColor:
                                    assistant.welcome_message &&
                                    assistant.welcome_message.trim() !== ""
                                      ? `${APP_COLOR}15`
                                      : "#e2e8f0",
                                  color:
                                    assistant.welcome_message &&
                                    assistant.welcome_message.trim() !== ""
                                      ? APP_COLOR
                                      : "#64748b",
                                }}
                              >
                                {assistant.welcome_message &&
                                assistant.welcome_message.trim() !== ""
                                  ? "Benutzerdefiniert"
                                  : "Standard"}
                              </span>
                            </div>
                            <textarea
                              ref={(el) => {
                                if (el) {
                                  welcomeMessageRefs.current.set(assistant.id, el);
                                }
                              }}
                              rows={4}
                              defaultValue={
                                assistant.welcome_message &&
                                assistant.welcome_message.trim() !== ""
                                  ? assistant.welcome_message
                                  : welcomeMessageTemplate
                              }
                              placeholder="Leer lassen, um den Standard-Text zu verwenden."
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (
                                  (!assistant.welcome_message ||
                                    assistant.welcome_message.trim() === "") &&
                                  value === welcomeMessageTemplate
                                ) {
                                  return;
                                }
                                handleWelcomeMessageUpdate(assistant.id, value);
                              }}
                              className="w-full text-sm text-slate-700 border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:border-transparent bg-white"
                              style={{ "--tw-ring-color": APP_COLOR } as React.CSSProperties}
                            />
                            <div className="mt-2 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleResetWelcomeMessage(assistant.id)}
                                className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-xs font-medium transition-colors border h-7 px-3 text-slate-600 border-slate-300 bg-white hover:bg-slate-50"
                              >
                                Standard verwenden
                              </button>
                              <span className="text-xs text-slate-400">
                                Platzhalter: {"{ASSISTANT_NAME}"}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {!isCurrent && (
                          <button
                            onClick={() => {
                              setSelectedAssistantId(assistant.id);
                              setCurrentAssistant(assistant.id);
                            }}
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-5"
                            style={{ backgroundColor: APP_COLOR }}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Aktivieren
                          </button>
                        )}
                        {role === "superadmin" && (
                          <button
                            onClick={() => handleDeleteAssistant(assistant.id)}
                            disabled={assistants.length <= 1}
                            className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-red-500 hover:text-red-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                            title={assistants.length <= 1 ? "Mindestens ein Assistent erforderlich" : "Assistent löschen"}
                          >
                            <svg
                              width="22"
                              height="22"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3 6H5H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Empty State */}
            {assistants.length === 0 && (
              <Card className="border-dashed border-2 border-slate-200 bg-slate-50/50">
                <CardContent className="p-12 flex flex-col items-center justify-center text-center">
                  <div
                    className="p-4 rounded-full mb-4"
                    style={{ backgroundColor: `${APP_COLOR}15` }}
                  >
                    <Bot className="h-12 w-12" style={{ color: APP_COLOR }} />
                  </div>
                  <h3 className="text-xl font-medium text-slate-700 mb-2">
                    Noch keine Assistenten
                  </h3>
                  <p className="text-sm text-slate-500 mb-6 max-w-sm">
                    Erstellen Sie Ihren ersten Assistenten, um mit der Konfiguration zu beginnen.
                  </p>
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-6"
                    style={{ backgroundColor: APP_COLOR }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Assistent erstellen
                  </button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Create Assistant Modal */}
      <CreateAssistantModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateAssistant}
        isLoading={isLoading}
        appColor={APP_COLOR}
      />

      {/* Profile Image Modal */}
      {isProfileModalOpen && (
        <ProfileImageModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          onProfileImageUpdate={handleProfileImageUpdate}
          onUserInfoUpdate={handleUserInfoUpdate}
          currentUser={currentUser}
        />
      )}
    </div>
  );
}